let map;
let mapElement;

// Defaults
const center = { lat: 53.60352032867638, lng: -1.7814485225240047 };
const zoom = 16;

// Wait for the Google Maps API to be available before initializing the map
const waitForGoogleMaps = () =>
  new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("Google Maps API did not become available."));
    }, 10000);

    const check = () => {
      if (window.google?.maps?.importLibrary) {
        clearTimeout(timeout);
        resolve();
        return;
      }

      setTimeout(check, 50);
    };

    check();
  });

// Initialize Google Maps
const initMap = async () => {
  const nextMapElement = document.getElementById("map");
  if (!nextMapElement || nextMapElement === mapElement) return;

  // Get map config
  const markers = JSON.parse(nextMapElement.dataset.markers || "[]");
  const mapZoom = Number(nextMapElement.dataset.zoom) || zoom;
  const mapCenter =
    nextMapElement.dataset.centerLat && nextMapElement.dataset.centerLng
      ? {
          lat: Number(nextMapElement.dataset.centerLat),
          lng: Number(nextMapElement.dataset.centerLng),
        }
      : null;

  try {
    await waitForGoogleMaps();
    const { InfoWindow, Map: GoogleMap } =
      await window.google.maps.importLibrary("maps");
    const { AdvancedMarkerElement } =
      await window.google.maps.importLibrary("marker");

    if (!nextMapElement.isConnected) return;

    // Build the map
    mapElement = nextMapElement;
    map = new GoogleMap(mapElement, {
      center:
        mapCenter ||
        (markers.length
          ? {
              lat: Number(markers[0].lat),
              lng: Number(markers[0].lng),
            }
          : center),
      zoom: mapZoom,
      zoomControl: true,
      mapId: "DEMO_MAP_ID",
      mapTypeControl: false,
    });

    const infoWindow = new InfoWindow();
    const openMarkerInfoWindow = (marker, { title, address, website }) => {
      const content = document.createElement("div");
      content.className = "map-tooltip";

      const titleElement = document.createElement("p");
      titleElement.className = "map-tooltip__title";
      titleElement.textContent = title;
      content.append(titleElement);

      if (address) {
        const addressElement = document.createElement("p");
        addressElement.className = "map-tooltip__address";
        addressElement.textContent = address;
        content.append(addressElement);
      }

      if (website) {
        const websiteElement = document.createElement("a");
        websiteElement.className = "map-tooltip__website";
        websiteElement.href = website;
        websiteElement.target = "_blank";
        websiteElement.rel = "nofollow noopener";
        websiteElement.textContent = "Visit website";
        content.append(websiteElement);
      }

      infoWindow.setContent(content);
      infoWindow.open({ map, anchor: marker });
    };

    // Add markers to the map
    const markerInstances = new Map();

    const selectMarker = (markerData) => {
      if (!markerData) return;

      map.panTo(markerData.marker.position);
      openMarkerInfoWindow(markerData.marker, markerData);
    };

    markers.forEach(({ id, lat, lng, title, address, website }) => {
      const markerContent = document.createElement("div");
      markerContent.className = "map-marker";
      markerContent.setAttribute("aria-hidden", "true");

      const marker = new AdvancedMarkerElement({
        map,
        position: { lat: Number(lat), lng: Number(lng) },
        title,
        content: markerContent,
      });

      const markerData = { marker, title, address, website };
      markerInstances.set(String(id), markerData);

      marker.addListener("click", () => {
        openMarkerInfoWindow(marker, markerData);
      });
    });

    document.querySelectorAll("[data-marker-id]").forEach((card) => {
      const selectCardMarker = () => {
        selectMarker(markerInstances.get(card.dataset.markerId));
      };

      card.addEventListener("click", (event) => {
        if (event.target.closest("a")) return;
        selectCardMarker();
      });
      card.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        selectCardMarker();
      });
    });

    // Fit the map bounds to include all markers if there are multiple markers,
    // unless an explicit center has been requested (e.g. a postcode search)
    if (!mapCenter && markers.length > 1) {
      const bounds = new window.google.maps.LatLngBounds();
      markers.forEach(({ lat, lng }) => {
        bounds.extend({ lat: Number(lat), lng: Number(lng) });
      });
      map.fitBounds(bounds);
    }
  } catch (error) {
    console.error(error);
  }
};

export default initMap;
